import { useEffect, useMemo, useState } from "react";

import {
  OP_LABELS,
  OPS_BY_TYPE,
  SCREEN_DERIVED_FIELDS,
  SCREEN_FIELD_TYPES,
  SCREEN_HEADER_FIELDS,
  STATUS_ENUM_VALUES,
  type LeafOp,
  type ScreenField,
  type ScreenFieldType,
  type ScreenPredicate,
} from "../../api/types/screenCriteria";
import {
  BACKEND_MAX_DEPTH,
  DEEPLY_NESTED_WARNING_DEPTH,
  appendChild,
  canAddGroupAtDepth,
  deserialiseTree,
  isGroupNode,
  isLeafNode,
  moveAtPath,
  newEmptyRoot,
  newGroup,
  newLeaf,
  removeAtPath,
  serialiseTree,
  updateAtPath,
  type GroupBuilderNode,
  type GroupConnector,
  type LeafBuilderNode,
} from "./builderTree";

/**
 * Shared multi-leaf predicate builder dialog (V2-2-U3 polish, session 268).
 *
 * Consumed by `ScreenBuilderDialog` (creates a SavedScreen via
 * `useCreateSavedScreen`) and `AlertRuleBuilderDialog` (creates an
 * AlertRule via `useCreateAlertRule`). Both predicate languages share
 * the same `ScreenCriteria` AST verbatim per V2-2-U4a Q-AST + ADR 0019
 * Consequences, so the field dropdown / op surface / value parsing are
 * identical -- the caller only varies the wire mutation + the surface
 * labels.
 *
 * Tree model + (de)serialisation live in
 * [builderTree.ts](./builderTree.ts); this file owns the recursive
 * renderer and tree-mutation handlers. Scope (session 268 Q159-Q161):
 *
 * * Single [+ Condition] / [+ Group] button per group with a per-group
 *   AND / OR / NOT toggle (Q159).
 * * Unbounded depth in the UI with a "Deeply nested" warning chip past
 *   depth 3 and a hard disable on [+ Group] at the backend's max
 *   nesting depth of 16 (Q160).
 * * Legacy single-leaf criteria wrap in an implicit top-level AND group
 *   on deserialise; the builder always edits a group root (Q161).
 *
 * Since V2-2-U3c the field dropdown surfaces the full 15-field set
 * (7 header + 8 V2-U2 derived) grouped via ``<optgroup>``.
 */
export type PredicateMutationLike = {
  mutate: (
    args: { name: string; criteria: Record<string, unknown> },
    options: { onSuccess: () => void },
  ) => void;
  isPending: boolean;
  isError: boolean;
  reset: () => void;
};

export type PredicateBuilderDialogProps = {
  /** Controlled-open: pass `open` + `onOpenChange` to let the parent
   * drive the dialog. When omitted the component renders its own
   * internal trigger button. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Label for the internal trigger button (only rendered when the
   * dialog is uncontrolled). */
  triggerLabel: string;
  /** `aria-label` on the dialog root -- read by screen readers + used
   * by tests to disambiguate two builder dialogs on the same page. */
  dialogAriaLabel: string;
  /** Heading + supporting copy. */
  title: string;
  description: string;
  /** Label for the name input. */
  nameLabel: string;
  /** Prefix for input IDs so two builder dialogs on the same page
   * don't collide on `htmlFor` attributes. */
  idPrefix: string;
  /** Error banner copy rendered when `mutation.isError`. */
  errorMessage: string;
  /** Submit-button label (e.g. "Save", "Create rule"). */
  submitLabel: string;
  mutation: PredicateMutationLike;
  /** When provided, seed the name input + builder tree on open (edit mode). */
  initialName?: string;
  initialCriteria?: Record<string, unknown> | null;
};

function seedRootFromCriteria(
  criteria: Record<string, unknown> | null | undefined,
): GroupBuilderNode {
  if (!criteria || Object.keys(criteria).length === 0) {
    return newEmptyRoot();
  }
  try {
    return deserialiseTree(criteria as unknown as ScreenPredicate);
  } catch {
    return newEmptyRoot();
  }
}

export function PredicateBuilderDialog({
  open: controlledOpen,
  onOpenChange,
  triggerLabel,
  dialogAriaLabel,
  title,
  description,
  nameLabel,
  idPrefix,
  errorMessage,
  submitLabel,
  mutation,
  initialName,
  initialCriteria,
}: PredicateBuilderDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (isControlled) {
      onOpenChange?.(next);
    } else {
      setInternalOpen(next);
    }
  };
  const [name, setName] = useState(initialName ?? "");
  const [root, setRoot] = useState<GroupBuilderNode>(() =>
    seedRootFromCriteria(initialCriteria),
  );
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Re-seed when the dialog opens (or the seed inputs change) so edit mode
  // reflects the currently-selected filter. The key folds name + criteria into
  // a stable string so a fresh object identity per render does not wipe
  // in-progress edits while the dialog stays open.
  const initialSeedKey = `${initialName ?? ""}::${JSON.stringify(
    initialCriteria ?? null,
  )}`;
  useEffect(() => {
    if (open) {
      setName(initialName ?? "");
      setRoot(seedRootFromCriteria(initialCriteria));
      setSubmitError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSeedKey]);

  const reset = () => {
    setName("");
    setRoot(newEmptyRoot());
    setSubmitError(null);
    setOpen(false);
    mutation.reset();
  };

  const handleAddCondition = (groupPath: ReadonlyArray<number>) => {
    setRoot((prev) => appendChild(prev, groupPath, newLeaf()));
  };

  const handleAddGroup = (groupPath: ReadonlyArray<number>) => {
    setRoot((prev) => appendChild(prev, groupPath, newGroup("and")));
  };

  const handleRemoveNode = (path: ReadonlyArray<number>) => {
    setRoot((prev) => removeAtPath(prev, path));
  };

  const handleMoveNode = (
    path: ReadonlyArray<number>,
    direction: "up" | "down",
  ) => {
    setRoot((prev) => moveAtPath(prev, path, direction));
  };

  const handleUpdateLeaf = (
    path: ReadonlyArray<number>,
    patch: Partial<Omit<LeafBuilderNode, "id" | "kind">>,
  ) => {
    setRoot((prev) =>
      updateAtPath(prev, path, (target) => {
        if (!isLeafNode(target)) {
          return target;
        }
        return { ...target, ...patch };
      }),
    );
  };

  const handleUpdateConnector = (
    path: ReadonlyArray<number>,
    connector: GroupConnector,
  ) => {
    setRoot((prev) =>
      updateAtPath(prev, path, (target) => {
        if (!isGroupNode(target)) {
          return target;
        }
        return { ...target, connector };
      }),
    );
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      return;
    }
    const result = serialiseTree(root);
    if (!result.ok) {
      setSubmitError(result.error);
      return;
    }
    setSubmitError(null);
    mutation.mutate(
      {
        name: trimmed,
        criteria: result.criteria as unknown as Record<string, unknown>,
      },
      { onSuccess: reset },
    );
  };

  const nameId = `${idPrefix}-name`;

  return (
    <>
      {!isControlled && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-control border border-outline-variant bg-surface px-3 py-1.5 text-sm font-medium text-on-surface transition-colors hover:bg-surface-container-high"
        >
          {triggerLabel}
        </button>
      )}
      {open ? (
        <div
          role="dialog"
          aria-label={dialogAriaLabel}
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        >
          <form
            onSubmit={handleSubmit}
            className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-control border border-outline-variant bg-surface shadow-lg"
          >
            <div className="border-b border-outline-variant p-4">
              <h2 className="font-display text-lg font-medium text-on-surface">
                {title}
              </h2>
              <p className="mt-1 text-sm text-on-surface-variant">
                {description}
              </p>
              <label
                htmlFor={nameId}
                className="mt-3 block text-sm font-medium text-on-surface"
              >
                {nameLabel}
              </label>
              <input
                id={nameId}
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoFocus
                required
                maxLength={200}
                className="mt-1 w-full rounded-control border border-outline-variant bg-surface px-3 py-1.5 text-sm text-on-surface"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <GroupView
                group={root}
                path={[]}
                depth={0}
                idPrefix={idPrefix}
                isRoot
                onAddCondition={handleAddCondition}
                onAddGroup={handleAddGroup}
                onRemove={handleRemoveNode}
                onMove={handleMoveNode}
                onUpdateLeaf={handleUpdateLeaf}
                onUpdateConnector={handleUpdateConnector}
              />
            </div>
            <div className="border-t border-outline-variant p-4">
              {submitError !== null ? (
                <p
                  className="mb-2 text-sm text-danger"
                  role="alert"
                  data-testid={`${idPrefix}-validation-error`}
                >
                  {submitError}
                </p>
              ) : null}
              {mutation.isError ? (
                <p className="mb-2 text-sm text-danger" role="alert">
                  {errorMessage}
                </p>
              ) : null}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={reset}
                  className="rounded-control border border-outline-variant bg-surface px-3 py-1.5 text-sm font-medium text-on-surface transition-colors hover:bg-surface-container-high"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={mutation.isPending || name.trim().length === 0}
                  className="rounded-control bg-primary px-3 py-1.5 text-sm font-medium text-on-primary transition-colors hover:bg-primary-hover disabled:opacity-60"
                >
                  {mutation.isPending ? "Saving…" : submitLabel}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : null}
    </>
  );
}

type GroupViewProps = {
  group: GroupBuilderNode;
  path: ReadonlyArray<number>;
  depth: number;
  idPrefix: string;
  isRoot: boolean;
  onAddCondition: (groupPath: ReadonlyArray<number>) => void;
  onAddGroup: (groupPath: ReadonlyArray<number>) => void;
  onRemove: (path: ReadonlyArray<number>) => void;
  onMove: (path: ReadonlyArray<number>, direction: "up" | "down") => void;
  onUpdateLeaf: (
    path: ReadonlyArray<number>,
    patch: Partial<Omit<LeafBuilderNode, "id" | "kind">>,
  ) => void;
  onUpdateConnector: (
    path: ReadonlyArray<number>,
    connector: GroupConnector,
  ) => void;
};

function GroupView({
  group,
  path,
  depth,
  idPrefix,
  isRoot,
  onAddCondition,
  onAddGroup,
  onRemove,
  onMove,
  onUpdateLeaf,
  onUpdateConnector,
}: GroupViewProps) {
  const connectorId = `${idPrefix}-connector-${group.id}`;
  const isNot = group.connector === "not";
  const canAddMore = !isNot || group.children.length === 0;
  const canNestGroup = canAddGroupAtDepth(depth);
  const showDeepWarning = depth > DEEPLY_NESTED_WARNING_DEPTH;

  return (
    <div
      data-testid={`group-${group.id}`}
      className={
        isRoot
          ? "rounded-control border border-outline-variant bg-surface-container p-3"
          : "rounded-control border border-outline-variant bg-surface-container-low p-3"
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={connectorId} className="sr-only">
          Group connector
        </label>
        <select
          id={connectorId}
          value={group.connector}
          onChange={(event) =>
            onUpdateConnector(path, event.target.value as GroupConnector)
          }
          aria-label="Group connector"
          className="rounded-control border border-outline-variant bg-surface px-2 py-1 text-xs font-medium uppercase tracking-wide text-on-surface"
        >
          <option value="and">AND</option>
          <option value="or">OR</option>
          <option value="not">NOT</option>
        </select>
        {showDeepWarning ? (
          <span
            data-testid={`group-${group.id}-deep-warning`}
            className="rounded-control bg-warning-container px-2 py-0.5 text-xs font-medium text-on-warning-container"
            title="Deeply nested groups can be hard to reason about."
          >
            Deeply nested
          </span>
        ) : null}
        <div className="ml-auto flex items-center gap-1">
          {canAddMore ? (
            <>
              <button
                type="button"
                onClick={() => onAddCondition(path)}
                className="rounded-control border border-outline-variant bg-surface px-2 py-1 text-xs font-medium text-on-surface transition-colors hover:bg-surface-container-high"
              >
                + Condition
              </button>
              <button
                type="button"
                onClick={() => onAddGroup(path)}
                disabled={!canNestGroup}
                title={
                  canNestGroup
                    ? undefined
                    : `Nesting cap reached (${BACKEND_MAX_DEPTH} levels).`
                }
                className="rounded-control border border-outline-variant bg-surface px-2 py-1 text-xs font-medium text-on-surface transition-colors hover:bg-surface-container-high disabled:opacity-60"
              >
                + Group
              </button>
            </>
          ) : null}
          {!isRoot ? (
            <button
              type="button"
              aria-label="Remove group"
              onClick={() => onRemove(path)}
              className="rounded-control border border-outline-variant bg-surface px-2 py-1 text-xs font-medium text-on-surface transition-colors hover:bg-surface-container-high"
            >
              ×
            </button>
          ) : null}
        </div>
      </div>
      {isNot && group.children.length > 1 ? (
        <p
          className="mt-2 text-xs text-danger"
          data-testid={`group-${group.id}-not-warning`}
        >
          NOT requires exactly one condition. Remove the extras or change
          the connector.
        </p>
      ) : null}
      <ul className="mt-3 space-y-2 pl-3">
        {group.children.length === 0 ? (
          <li className="text-xs italic text-on-surface-variant">
            {isRoot
              ? "No conditions yet — add one above to filter; leave empty to match every trade."
              : "Empty group — add a condition above."}
          </li>
        ) : null}
        {group.children.map((child, index) => {
          const childPath = [...path, index];
          return (
            <li key={child.id}>
              {isGroupNode(child) ? (
                <ChildShell
                  index={index}
                  total={group.children.length}
                  path={childPath}
                  onMove={onMove}
                  onRemove={onRemove}
                  removeLabel="Remove group"
                >
                  <GroupView
                    group={child}
                    path={childPath}
                    depth={depth + 1}
                    idPrefix={idPrefix}
                    isRoot={false}
                    onAddCondition={onAddCondition}
                    onAddGroup={onAddGroup}
                    onRemove={onRemove}
                    onMove={onMove}
                    onUpdateLeaf={onUpdateLeaf}
                    onUpdateConnector={onUpdateConnector}
                  />
                </ChildShell>
              ) : (
                <ChildShell
                  index={index}
                  total={group.children.length}
                  path={childPath}
                  onMove={onMove}
                  onRemove={onRemove}
                  removeLabel="Remove condition"
                >
                  <LeafView
                    leaf={child}
                    path={childPath}
                    idPrefix={idPrefix}
                    onUpdate={onUpdateLeaf}
                  />
                </ChildShell>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

type ChildShellProps = {
  index: number;
  total: number;
  path: ReadonlyArray<number>;
  onMove: (path: ReadonlyArray<number>, direction: "up" | "down") => void;
  onRemove: (path: ReadonlyArray<number>) => void;
  removeLabel: string;
  children: React.ReactNode;
};

function ChildShell({
  index,
  total,
  path,
  onMove,
  onRemove,
  removeLabel,
  children,
}: ChildShellProps) {
  const canUp = index > 0;
  const canDown = index < total - 1;
  return (
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">{children}</div>
      <div className="flex flex-col items-center gap-1 pt-1">
        <button
          type="button"
          aria-label="Move up"
          disabled={!canUp}
          onClick={() => onMove(path, "up")}
          className="rounded-control border border-outline-variant bg-surface px-1.5 py-0.5 text-xs text-on-surface transition-colors hover:bg-surface-container-high disabled:opacity-40"
        >
          ↑
        </button>
        <button
          type="button"
          aria-label="Move down"
          disabled={!canDown}
          onClick={() => onMove(path, "down")}
          className="rounded-control border border-outline-variant bg-surface px-1.5 py-0.5 text-xs text-on-surface transition-colors hover:bg-surface-container-high disabled:opacity-40"
        >
          ↓
        </button>
        <button
          type="button"
          aria-label={removeLabel}
          onClick={() => onRemove(path)}
          className="rounded-control border border-outline-variant bg-surface px-1.5 py-0.5 text-xs text-on-surface transition-colors hover:bg-surface-container-high"
        >
          ×
        </button>
      </div>
    </div>
  );
}

type LeafViewProps = {
  leaf: LeafBuilderNode;
  path: ReadonlyArray<number>;
  idPrefix: string;
  onUpdate: (
    path: ReadonlyArray<number>,
    patch: Partial<Omit<LeafBuilderNode, "id" | "kind">>,
  ) => void;
};

function LeafView({ leaf, path, idPrefix, onUpdate }: LeafViewProps) {
  const fieldId = `${idPrefix}-field-${leaf.id}`;
  const opId = `${idPrefix}-op-${leaf.id}`;
  const valueId = `${idPrefix}-value-${leaf.id}`;
  const fieldType: ScreenFieldType | null =
    leaf.field === "" ? null : SCREEN_FIELD_TYPES[leaf.field];
  const allowedOps = useMemo<ReadonlyArray<LeafOp>>(
    () => (fieldType ? OPS_BY_TYPE[fieldType] : []),
    [fieldType],
  );
  const valueDisabled =
    leaf.op === "" || leaf.op === "is_null" || leaf.op === "is_not_null";

  return (
    <div className="rounded-control border border-outline-variant bg-surface p-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div>
          <label
            htmlFor={fieldId}
            className="block text-xs font-medium text-on-surface-variant"
          >
            Field
          </label>
          <select
            id={fieldId}
            value={leaf.field}
            onChange={(event) =>
              onUpdate(path, {
                field: event.target.value as ScreenField | "",
                op: "",
                value: "",
              })
            }
            className="mt-1 w-full rounded-control border border-outline-variant bg-surface px-2 py-1 text-sm text-on-surface"
          >
            <option value="">— pick —</option>
            <optgroup label="Header">
              {SCREEN_HEADER_FIELDS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </optgroup>
            <optgroup label="Derived">
              {SCREEN_DERIVED_FIELDS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </optgroup>
          </select>
        </div>
        <div>
          <label
            htmlFor={opId}
            className="block text-xs font-medium text-on-surface-variant"
          >
            Operator
          </label>
          <select
            id={opId}
            value={leaf.op}
            onChange={(event) =>
              onUpdate(path, { op: event.target.value as LeafOp | "" })
            }
            disabled={leaf.field === ""}
            className="mt-1 w-full rounded-control border border-outline-variant bg-surface px-2 py-1 text-sm text-on-surface disabled:opacity-60"
          >
            <option value="">—</option>
            {allowedOps.map((o) => (
              <option key={o} value={o}>
                {OP_LABELS[o]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor={valueId}
            className="block text-xs font-medium text-on-surface-variant"
          >
            Value
            {fieldType === "enum" && leaf.field === "status" ? (
              <span className="ml-1 text-xs text-on-surface-variant">
                (one of {STATUS_ENUM_VALUES.join(", ")})
              </span>
            ) : null}
            {leaf.op === "in" ? (
              <span className="ml-1 text-xs text-on-surface-variant">
                (comma-separated)
              </span>
            ) : null}
          </label>
          <input
            id={valueId}
            type="text"
            value={leaf.value}
            onChange={(event) => onUpdate(path, { value: event.target.value })}
            disabled={valueDisabled}
            className="mt-1 w-full rounded-control border border-outline-variant bg-surface px-2 py-1 text-sm text-on-surface disabled:opacity-60"
          />
        </div>
      </div>
    </div>
  );
}
